import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import gridStyles from 'src/assets/style/grid.module.css';
import { PageSection } from 'js/components/PageSection';
import { mParticleEventType, Stack, useDevice } from '@treatwell/ui';
import { createOpeningHoursViewModel } from 'js/model/view/venue-opening-times';
import { formatAddress } from 'js/helpers/formatters';
import { VenueOpeningTimes } from 'js/components/VenueOpeningTimes';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { WhenVisible } from 'js/components/WhenVisible';
import { getStaticMapUrl, VenueLocation } from 'js/helpers/static-map-url';
import { VenueOutput } from 'js/model/rainbow/venue/VenueOutput';
import { hasOpeningHours } from 'js/helpers/venue-relative-opening-time';
import { useIntersectionObserver } from 'js/hooks/useIntersectionObserver';
import { trackEvent } from 'js/mparticle/mParticleTracking';
import { generateVenueTrackingAttributes } from 'js/mparticle/generateVenueTrackingAttributes';
import { VenueDescription } from './VenueDescription';
import { VenueContacts } from './VenueContacts';
import venueContactsStyles from './VenueContacts.module.css';
import venuePageStyles from '../styles.module.css';
import { trackMParticleVenuePageEvent } from '../tracking';

const aboutSectionId = 'about';
const descriptionSectionId = 'description';

interface Props {
  venue: VenueOutput;
  openingHoursClosedLabel: string;
  dayNames: CmsBrowsePage['common']['day-names'];
  dayNamesShort: CmsBrowsePage['common']['calendar']['day-names-short'];
  countryCode: string;
  channelCode: string;
  titleAbout: string;
}

export function VenueAboutSection(props: Props): React.ReactElement {
  const formattedAddressLines = React.useRef<string[]>(
    formatAddress(
      props.venue.location.address.addressLines,
      props.venue.location.address.postalCode,
      props.channelCode === 'WHN_GB' || props.channelCode === 'WHN_IE'
        ? 'newline'
        : 'prefix'
    )
  );

  const { isMobile } = useDevice();

  const handleMapClick = () => {
    trackMParticleVenuePageEvent(
      'venue_map_clicked',
      props.countryCode,
      isMobile,
      venue
    );
  };

  const renderMap = (): React.ReactNode => {
    const staticMapUrl = getStaticMapUrl(props.venue.location as VenueLocation);
    const query = encodeURIComponent(formattedAddressLines.current.join(' '));

    return (
      <WhenVisible placeholder={<span data-cy="WhenVisiblePlaceholder" />}>
        <a
          className={venueContactsStyles.staticMapImageContainer}
          href={`https://www.google.com/maps/search/?api=1&query=${query}`}
          target="_blank"
          onClick={handleMapClick}
          rel="noreferrer"
          data-cy="VenueMap"
        >
          <img
            src={staticMapUrl}
            className={venueContactsStyles.staticMapImage}
          />
        </a>
      </WhenVisible>
    );
  };

  const venue = props.venue;

  const openingTimes = createOpeningHoursViewModel(
    venue.openingHours,
    props.openingHoursClosedLabel,
    props.dayNames,
    props.dayNamesShort,
    props.countryCode
  );

  const classes = clsx(
    [gridStyles['col-xs-12']],
    [gridStyles['col-sm-6']],
    [gridStyles['col-md-7']],
    venueContactsStyles.mapContainer
  );
  const venueDescriptionRef = useRef(null);
  const venueDescriptionEntry = useIntersectionObserver(venueDescriptionRef, {
    threshold: 0,
  });
  const isVenueDescriptionVisible = !!venueDescriptionEntry?.isIntersecting;

  useEffect(() => {
    if (isVenueDescriptionVisible && venue) {
      trackEvent(
        'about_venue_viewed',
        mParticleEventType.Other,
        generateVenueTrackingAttributes(venue, 'venue/')
      );
    }
  }, [isVenueDescriptionVisible, venue]);

  return (
    <>
      <PageSection id={aboutSectionId} title={props.titleAbout}>
        <div className={clsx(gridStyles.row, venuePageStyles.addressRow)}>
          <div className={classes}>{renderMap()}</div>
          <VenueContacts
            name={venue.name}
            addressLines={formattedAddressLines.current}
          />
        </div>
      </PageSection>
      <PageSection id={descriptionSectionId}>
        <div
          ref={venueDescriptionRef}
          className={venuePageStyles.descriptionContainer}
          id="venue-description"
        >
          <div className={venuePageStyles.gridDescription}>
            <Stack space="xs">
              <VenueDescription htmlDescription={venue.description!} />
            </Stack>
          </div>
          {hasOpeningHours(venue.openingHours) && (
            <VenueOpeningTimes
              items={openingTimes}
              positioningClassName={venuePageStyles.gridOpeningTimes}
            />
          )}
        </div>
      </PageSection>
    </>
  );
}
